import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import RichTextComposer from '../../my_inbox/RichTextComposer/RichTextComposer';
import AttachedExistingEntity from './AttachedExistingEntity';
import AttachedNewEntity from './AttachedNewEntity';
import AiDetectorButton from '../../pitch/pangram/ai_detector_button';
import { autoSavePitchText, cancelAutosave } from '../services/autosave';

// Keep the counter hidden for short pitches; reveal it once it's long enough to matter.
const CHAR_COUNTER_REVEAL_AT = 1000;

const PITCH_PLACEHOLDER = 'Write your pitch here. Include:\n\n'
  + '• A brief introduction (you or your expert) with relevant credentials\n'
  + '• A clear response to the opportunity or questions\n'
  + '• Quotable insights or key points\n'
  + '• Availability and suggested next steps\n\n'
  + 'Example: "I\'m a NASM Certified Personal Trainer with 10+ years of experience, '
  + 'and I\'d be happy to share insights on…"';

// The messaging RichTextComposer (bold/italic/link/lists — content_format "html"), with the
// wizard's placeholder and Check-for-AI in the bottom action bar per design.
function PitchTextEditor({
                           pitchText,
                           setPitchText,
                           pitchTextEdited,
                           attachedEntities,
                           deleteExistingEntityFromPitch,
                           deleteNewEntity,
                           requestId,
                           sourceRequestType,
                           pitchId,
                           overLimit,
                           charCount,
                           charLimit,
                           aiDetectorEnabled,
                         }) {
  const [autosaveStatus, setAutosaveStatus] = useState('');

  const handlePitchTextChange = (event) => {
    const newText = event.target.value;
    setPitchText(newText);

    if (pitchId && newText !== pitchText) {
      autoSavePitchText(
        pitchId,
        newText,
        requestId,
        () => {
          setAutosaveStatus('saved');
          setTimeout(() => setAutosaveStatus(''), 2000);
        },
        () => {
          setAutosaveStatus('error');
          setTimeout(() => setAutosaveStatus(''), 3000);
        },
      );
    }
  };

  useEffect(() => () => {
      cancelAutosave();
    }, []);

  const welcome = sourceRequestType === 'welcome';
  return (
    <div
      className={classnames('pitch-text-editor pitch-rich-composer bg-white d-flex flex-column border rounded mb-3 w-100 position-relative', {
        'over-limit': overLimit,
      })}
    >
      <RichTextComposer
        id="pitch_text"
        value={pitchText}
        onChange={handlePitchTextChange}
        placeholder={welcome ? 'Share a quick intro...' : PITCH_PLACEHOLDER}
        hasAttachments={attachedEntities.length > 0}
      >
        <div className="d-flex flex-column w-100 gap-1">
          <div className="d-flex align-items-center w-100 gap-2">
            <span className="small">
              {autosaveStatus === 'saved' && (
                <span className="text-success"><i className="fa-solid fa-check" /> Saved</span>
              )}
              {autosaveStatus === 'error' && (
                <span className="text-danger"><i className="fa-solid fa-exclamation-triangle" /> Failed to save</span>
              )}
              {!autosaveStatus && pitchTextEdited && (
                <span className="text-secondary"><i className="fa-solid fa-circle" /> Edited</span>
              )}
            </span>
            <div className="ms-auto d-flex align-items-center gap-2">
              {charLimit != null && charCount >= CHAR_COUNTER_REVEAL_AT && (
                <span
                  className={classnames('pitch-char-count', { 'over-limit': overLimit })}
                  data-testid="char-count"
                  style={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {charCount}/{charLimit}
                </span>
              )}
              {aiDetectorEnabled && (
                <AiDetectorButton checkAiDetectorVisibility={true} text={pitchText} ahoyObjectGid={`gid://vested/SourceRequest/${requestId}`} />
              )}
            </div>
          </div>
          {attachedEntities.length > 0 && (
            <div className="attached-entities-row d-flex align-items-center flex-wrap">
              {attachedEntities.map((entity) => (
                <React.Fragment key={entity.existing?.localId || entity.new?.localId}>
                  {entity.existing && (
                    <AttachedExistingEntity
                      key={entity.existing.pitchedEntityId}
                      firstName={entity.existing.firstName}
                      isPaid={entity.existing.isPaid}
                      isViewersOwnProfile={entity.existing.isCurrentUser}
                      name={entity.existing.name}
                      profileImageUrl={entity.existing.profileImageUrl}
                      promoEntityId={entity.existing.id}
                      removeCallback={deleteExistingEntityFromPitch}
                      subheading={entity.existing.subheading}
                    />
                  )}
                  {entity.new && (
                    <AttachedNewEntity
                      key={entity.new.localId}
                      localId={entity.new.localId}
                      name={entity.new.name}
                      removeCallback={deleteNewEntity}
                      subheading={entity.new.subheading}
                      type={entity.new.type}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
      </RichTextComposer>
    </div>
  );
}

PitchTextEditor.propTypes = {
  pitchText: PropTypes.string,
  setPitchText: PropTypes.func.isRequired,
  pitchTextEdited: PropTypes.bool.isRequired,
  attachedEntities: PropTypes.arrayOf(PropTypes.object).isRequired,
  deleteExistingEntityFromPitch: PropTypes.func.isRequired,
  deleteNewEntity: PropTypes.func.isRequired,
  requestId: PropTypes.number.isRequired,
  sourceRequestType: PropTypes.string,
  pitchId: PropTypes.string,
  overLimit: PropTypes.bool,
  charCount: PropTypes.number,
  charLimit: PropTypes.number,
  aiDetectorEnabled: PropTypes.bool,
};

PitchTextEditor.defaultProps = {
  pitchText: undefined,
  sourceRequestType: '',
  pitchId: undefined,
  overLimit: false,
  charCount: 0,
  charLimit: null,
  aiDetectorEnabled: false,
};

export default PitchTextEditor;
